<app-protected-content
  [requiredRoles]="requiredRoles"
  [requireApprovedOrganization]="true"
  *transloco="let t"
>
  @if (isDeploymentLoading() || isAppsLoading()) {
    <div class="skeleton-loading">
      <htx-skeleton width="100%" height="9rem" borderRadius="8px"></htx-skeleton>
      <htx-skeleton width="35%" height="1.5rem" borderRadius="4px"></htx-skeleton>
      <div class="app-grid">
        <htx-skeleton width="100%" height="14rem" borderRadius="8px"></htx-skeleton>
        <htx-skeleton width="100%" height="14rem" borderRadius="8px"></htx-skeleton>
        <htx-skeleton width="100%" height="14rem" borderRadius="8px"></htx-skeleton>
      </div>
    </div>
  } @else {
    <!-- Connector Section: Not Yet Deployed -->
    @if (!isDeploymentLoading() && !connector()) {
      <div class="connector-deploy-card">
        <div class="connector-deploy-card__icon">
          <span class="material-icons">dns</span>
        </div>
        <div class="connector-deploy-card__body">
          <h3 class="connector-deploy-card__title">
            {{ t('appManagement.connector.notDeployed.title') }}
          </h3>
          <p class="connector-deploy-card__desc">
            {{ t('appManagement.connector.notDeployed.description') }}
          </p>
        </div>
        <div class="connector-deploy-card__actions">
          <htx-button
            variant="primary"
            size="small"
            icon="rocket_launch"
            [disabled]="isDeployingConnector() || isRancherEnv"
            (buttonClick)="onDeployConnector()"
          >
            {{ t('appManagement.connector.notDeployed.deployButton') }}
          </htx-button>
        </div>
      </div>
    }

    <!-- Connector Section: Deployed -->
    @if (connector(); as conn) {
      <div class="connector-card">
        <div class="connector-card__icon">
          <span class="material-icons">dns</span>
        </div>
        <div class="connector-card__body">
          <h3 class="connector-card__title">{{ t('appManagement.connector.title') }}</h3>
          <p class="connector-card__desc">{{ t('appManagement.connector.description') }}</p>
          <div class="connector-card__footer">
            @if (conn.state === 'PROVISIONING') {
              <!-- Initial connector provisioning -->
              <htx-status-indicator [status]="'DOWN'" />
              <span class="connector-card__state connector-card__state--provisioning">
                {{ t('appManagement.connector.state.PROVISIONING') }}<htx-loading-dots />
              </span>
            } @else if (conn.state === 'ACTIVE') {
              @if (isAppInstallInProgress()) {
                <!-- App installation in progress -->
                <htx-status-indicator [status]="conn.status" />
                <span class="connector-card__state connector-card__state--app-installing">
                  {{ t('appManagement.connector.appInstalling') }}<htx-loading-dots />
                </span>
              } @else if (isAppUninstallInProgress()) {
                <!-- App uninstallation in progress -->
                <htx-status-indicator [status]="conn.status" />
                <span class="connector-card__state connector-card__state--app-installing">
                  {{ t('appManagement.connector.appUninstalling') }}<htx-loading-dots />
                </span>
              } @else {
                <!-- ACTIVE: show status indicator + tooltip (no separate health/sync badges) -->
                <span
                  class="connector-tooltip-anchor"
                  tabindex="0"
                  [htxTooltip]="connectorTooltip()"
                  htxTooltipMultiline
                >
                  <htx-status-indicator [status]="conn.status" />
                </span>
              }
            } @else {
              <!-- Non-ACTIVE states (SUSPENDED, REMOVING) -->
              <htx-status-indicator [status]="conn.status" />
              <span
                class="connector-card__state"
                [class.connector-card__state--suspended]="conn.state === 'SUSPENDED'"
              >
                {{ t('appManagement.connector.state.' + conn.state) }}
              </span>
            }
          </div>
        </div>
        <div class="connector-card__actions">
          @if (conn.state === 'ACTIVE' && conn.managementUrl) {
            <htx-button
              variant="primary"
              size="small"
              icon="open_in_new"
              (buttonClick)="onOpenConnectorManagement()"
            >
              {{ t('appManagement.connector.openManagement') }}
            </htx-button>
          }
        </div>
      </div>
    }

    <!-- My Apps Section -->
    <h2 class="section-title">{{ t('appManagement.apps.title') }}</h2>

    @if (computedApps().length === 0 && !connectorManagementApp()) {
      <div class="empty-state">
        <span class="material-icons empty-state__icon">inventory_2</span>
        <p class="empty-state__text">{{ t('appManagement.apps.empty') }}</p>
        <p class="empty-state__hint">{{ t('appManagement.apps.emptyHint') }}</p>
      </div>
    } @else {
      <div class="app-grid">
        @if (connectorManagementApp(); as connApp) {
          <div class="app-card app-card--deployed">
            <div class="app-card__header">
              <span class="material-icons app-card__icon">settings</span>
              <span class="app-card__status-badge app-card__status-badge--deployed">
                {{ t('appManagement.apps.status.DEPLOYED') }}
              </span>
            </div>
            <h3 class="app-card__title">{{ t('appManagement.apps.connectorManagement.name') }}</h3>
            <p class="app-card__desc">
              {{ t('appManagement.apps.connectorManagement.description') }}
            </p>
            <div class="app-card__actions">
              <htx-button
                variant="primary"
                size="small"
                icon="open_in_new"
                (buttonClick)="onOpenConnectorManagement()"
              >
                {{ t('appManagement.apps.openApp') }}
              </htx-button>
            </div>
          </div>
          @if (computedApps().length === 0) {
            <div class="app-card app-card--placeholder">
              <span class="material-icons app-card__icon app-card__icon--muted">inventory_2</span>
              <div class="app-card__placeholder-body">
                <h3 class="app-card__title">{{ t('appManagement.apps.useCaseEmpty.title') }}</h3>
                <p class="app-card__desc">{{ t('appManagement.apps.useCaseEmpty.hint') }}</p>
              </div>
            </div>
          }
        }
        @for (app of computedApps(); track app.id) {
          <div
            class="app-card"
            [class.app-card--deployed]="app.deploymentState === 'DEPLOYED'"
            [class.app-card--deploying]="app.deploymentState === 'DEPLOYING'"
            [class.app-card--error]="app.deploymentState === 'ERROR'"
          >
            <div class="app-card__header">
              <span class="material-icons app-card__icon">{{ app.icon }}</span>
              <span
                class="app-card__status-badge"
                [class.app-card__status-badge--deployed]="app.deploymentState === 'DEPLOYED'"
                [class.app-card__status-badge--not-deployed]="
                  app.deploymentState === 'NOT_DEPLOYED'
                "
                [class.app-card__status-badge--deploying]="app.deploymentState === 'DEPLOYING'"
                [class.app-card__status-badge--error]="app.deploymentState === 'ERROR'"
              >
                {{ t('appManagement.apps.status.' + app.deploymentState) }}
                @if (app.deploymentState === 'DEPLOYING') {
                  <htx-loading-dots />
                }
              </span>
            </div>
            <h3 class="app-card__title">{{ t(app.nameKey) }}</h3>
            <p class="app-card__desc">{{ t(app.descriptionKey) }}</p>
            <div class="app-card__actions">
              @if (app.deploymentState === 'NOT_DEPLOYED') {
                <htx-button
                  variant="primary"
                  size="small"
                  icon="power_settings_new"
                  [disabled]="isLoading(app.id) || isRancherEnv"
                  (buttonClick)="onDeploy(app)"
                >
                  {{ t('appManagement.apps.deploy') }}
                </htx-button>
              } @else if (app.deploymentState === 'DEPLOYED') {
                @if (app.appUrl) {
                  <htx-button
                    variant="primary"
                    size="small"
                    icon="open_in_new"
                    [disabled]="isLoading(app.id)"
                    (buttonClick)="onOpenApp(app)"
                  >
                    {{ t('appManagement.apps.openApp') }}
                  </htx-button>
                }
                <htx-button
                  variant="danger"
                  size="small"
                  icon="power_settings_new"
                  [disabled]="isLoading(app.id) || isRancherEnv"
                  (buttonClick)="onUndeploy(app)"
                >
                  {{ t('appManagement.apps.undeploy') }}
                </htx-button>
              }
            </div>
          </div>
        }
      </div>
    }
  }
</app-protected-content>
